import { PagesizeType } from '../../service/pagination/pagesize-type';
import { PaymentStatus } from './../../models/payment-status.enum';
import { OnInit, Component, Input, ViewChild, OnDestroy } from '@angular/core';
import { Rsvp } from '../../models/rsvp';
import { List } from 'immutable';
import { Observable } from 'rxjs/Observable';
import { RsvpStore } from '../../service/rsvp.store';
import { OnyxEvent } from '../../models/event';
import { ConfirmationModalComponent } from '../../../shared/confirmation-modal/confirmation-modal.component';
import { ReservationCompareStore } from '../../service/reservation-compare-store';
import { RsvpModalComponent } from '../rsvp-modal/rsvp-modal.component';
import { SummaryRecordTypeEnum } from '../../models/summary-record-type.enum';
import { PaymentStore } from '../../service/payment.store';
import { Page } from '../../../shared/models/page';
import { Subscription } from 'rxjs/Subscription';
import { ReservationCompare } from '../../models/reservation-compare';
import { ReservationUtils } from '../../utils/reservation-utils';
import { UserLocalStorageService } from '../../../permission/services/user.local.storage.service';
import { AuthenticationStore } from 'app/permission/services/authentication.store';
import { Party } from 'app/event/models/party';
import { EventValidationService } from 'app/event/service/event-validation.service';

@Component({
  selector: 'pioneer-rsvp-summary',
  templateUrl: './rsvp-summary.component.html'
})
export class RsvpSummaryComponent implements OnInit, OnDestroy {

  @Input() vendor: Party;

  public rsvpToRemove: Rsvp;

  @ViewChild(ConfirmationModalComponent)
  public confirmationModal: ConfirmationModalComponent;

  @ViewChild(RsvpModalComponent) rsvpModal: RsvpModalComponent;

  public onyxEvent: OnyxEvent;

  public headerRecordType: SummaryRecordTypeEnum;

  displayDropdown: boolean;

  public rsvpPage: Page<ReservationCompare>;

  private rsvpSubscription: Subscription = new Subscription();

  pagesizeType = PagesizeType.RSVP_SUMMARY;

  rsvpDetailLink: string;

  @Input('event')
  set event(event: OnyxEvent) {
    this.onyxEvent = event;
  }

  canChangeRSVPs$: Observable<boolean>;

  constructor(private rsvpStore: RsvpStore,
              private reservationCompareStore: ReservationCompareStore,
              private paymentStore: PaymentStore,
              private authenticationStore: AuthenticationStore,
              private userLocalStorageService: UserLocalStorageService,
              private eventValidationService: EventValidationService) {
  }

  ngOnInit() {
    if (!this.onyxEvent) {
      throw new Error('The "event" attribute must be specified to use the "pioneer-rsvp-summary" component');
    }

    this.rsvpPage = Page.empty<ReservationCompare>();
    this.rsvpPage.size = this.userLocalStorageService.getPagesize(PagesizeType.RSVP_SUMMARY);

    this.rsvpSubscription.add(this.getFilteredRsvps().subscribe(reservationList => {
      const page = this.rsvpPage.number;
      const itemsPerPage = this.rsvpPage.size;

      this.rsvpPage.content = List<ReservationCompare>(
        reservationList.slice(page * itemsPerPage, (page + 1) * itemsPerPage)
      );
      this.rsvpPage.totalElements = reservationList.size;
      this.rsvpPage.numberOfElements = this.rsvpPage.content.size;
    }));

    this.headerRecordType = SummaryRecordTypeEnum.RSVP;

    if (!this.onyxEvent.eventId) {
      this.displayDropdown = true;
    } else {
      this.paymentStore.getVendorFinalPayment(this.vendor.eventPartyId).take(1).subscribe(lastFinalPayment => {
        const hasFinalPayment = Boolean(lastFinalPayment && lastFinalPayment.eventPaymentId);
        this.displayDropdown = !hasFinalPayment || [PaymentStatus.REQUEST_REJECTED, PaymentStatus.REJECTED_WF_APPROVAL].some(
          status => status === lastFinalPayment.status);
      });
    }

    this.canChangeRSVPs$ = this.eventValidationService.canChangeRsvps();

    this.rsvpDetailLink = '/rsvp/detail';
  }

  ngOnDestroy(): void {
    this.rsvpSubscription.unsubscribe();
  }

  get isRsvpListEmpty(): Observable<boolean> {
    return ReservationUtils.isListEmpty(this.getFilteredRsvps());
  }

  confirmBeforeDelete(event: any, rsvp: Rsvp) {
    event.preventDefault();
    this.rsvpToRemove = rsvp;
    this.confirmationModal.open();
  }

  confirmHandler(deleteConfirmed: boolean) {
    if (deleteConfirmed) {
      this.deleteRsvp(this.rsvpToRemove);
    }

    this.rsvpToRemove = null;
  }

  openRsvpModal(event: any, rsvp: Rsvp, vendor: Party) {
    event.preventDefault();
    this.rsvpModal.open(rsvp, vendor);
  }

  deleteRsvp(rsvp: Rsvp): Observable<boolean> {
    const obs = this.rsvpStore.deleteRsvp(rsvp);
    obs.subscribe(() => {
      if (this.isMultiVendor()) {
        this.reservationCompareStore.refreshCurrentRsvpList(this.onyxEvent.eventId);
      } else {
        this.reservationCompareStore.refreshCurrentRsvpListByParty(this.onyxEvent.eventId, this.onyxEvent.eventProperty.eventPartyId);
      }
    });
    return obs;
  }

  onRefreshRsvpList(pageNumber: number) {
    const itemsPerPage = this.userLocalStorageService.getPagesize(PagesizeType.RSVP_SUMMARY);
    this.getFilteredRsvps().take(1).subscribe(reservationList => {
      this.rsvpPage.content = List<ReservationCompare>(
        reservationList.slice(pageNumber * itemsPerPage, (pageNumber + 1) * itemsPerPage)
      );
      this.rsvpPage.number = pageNumber;
      this.rsvpPage.size = itemsPerPage;
      this.rsvpPage.totalElements = reservationList.size;
      this.rsvpPage.numberOfElements = this.rsvpPage.content.size;
    });
  }

  getFilteredRsvps(): Observable<List<ReservationCompare>> {
    return this.reservationCompareStore.getVendorCompareList(this.vendor.eventPartyId)
      .map(reservationList => ReservationUtils.onlyRsvpFilter(reservationList));
  }

  isMultiVendor() {
    return this.authenticationStore.hasPermission('MultiVendor');
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/rsvp/rsvp-summary/rsvp-summary.component.ts